import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../header/Header";
import Axios from "axios";
import { toast } from "react-toastify";
import {
  LuCheck,
  LuLoader2,
  LuPencil,
  LuRotateCcw,
  LuSearch,
  LuUserMinus,
  LuUserPlus,
  LuUsers,
} from "react-icons/lu";
import config from "../../config/config";
import queryString from "query-string";
import { UiButton, INPUT } from "../ui/ui";
import { ModalShell } from "../dashboard/WeeklyPriorities";
import "../userList/userList.css";

const MAX_PL = 21;
const MAX_OPTIONAL = 2;
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getInitials = (name, email) => {
  const parts = String(name || email || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

// Opening balances for new users; existing balances aren't editable (they
// change only through leave applications and the quarterly credit).
const EMPTY_FORM = { name: "", email: "", plBalance: "0", chBalance: String(MAX_OPTIONAL) };

const FieldLabel = ({ htmlFor, children, hint }) => (
  <label
    htmlFor={htmlFor}
    className="tw-m-0 tw-mb-1.5 tw-flex tw-items-baseline tw-justify-between tw-text-sm tw-font-medium tw-text-foreground"
  >
    <span>
      {children}
      <span className="tw-ml-0.5 tw-text-destructive">*</span>
    </span>
    {hint && (
      <span className="tw-text-xs tw-font-normal tw-text-muted-foreground">{hint}</span>
    )}
  </label>
);

const UserFormModal = ({ show, handleClose, user, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset whenever the modal opens, so "Create" never shows the last edited user
  useEffect(() => {
    if (!show) return;
    setForm(
      user
        ? {
            name: user.name || "",
            email: user.email || "",
            plBalance: user.plBalance ?? "",
            chBalance: user.chBalance ?? "",
          }
        : EMPTY_FORM,
    );
    setSubmitted(false);
  }, [show, user]);

  const isEdit = !!user?.id;
  const balanceError = (value, max) =>
    isEdit
      ? ""
      : value === "" || Number(value) < 0 || Number(value) > max
        ? `Enter 0 to ${max}`
        : "";
  const errors = {
    name: !String(form.name).trim() ? "Enter a name" : "",
    email: !EMAIL_RX.test(String(form.email).trim()) ? "Enter a valid email" : "",
    plBalance: balanceError(form.plBalance, MAX_PL),
    chBalance: balanceError(form.chBalance, MAX_OPTIONAL),
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const save = () => {
    setSubmitted(true);
    if (hasErrors || saving) return;
    const data = {
      name: String(form.name).trim(),
      email: String(form.email).trim(),
    };
    if (isEdit) {
      data.userId = user.id;
    } else {
      data.plBalance = Number(form.plBalance);
      data.chBalance = Number(form.chBalance);
    }
    const api = user?.id ? "/user/updateUserDetails" : "/user/insertUserDetails";

    setSaving(true);
    Axios.post(config.API_BASE + api, data)
      .then((response) => {
        if (response.data?.status) {
          toast.success(user ? "User updated" : "User created");
          onSaved();
          handleClose();
        } else {
          toast.error(response.data?.msg || "Couldn't save this user");
        }
      })
      .catch((error) => {
        console.log("error", error);
        toast.error("Something went wrong. Please try again.");
      })
      .finally(() => setSaving(false));
  };

  const fieldClass = (key) =>
    `${INPUT} ${submitted && errors[key] ? "tw-border-destructive tw-ring-2 tw-ring-destructive/15" : ""}`;
  const errorText = (key) =>
    submitted && errors[key] ? (
      <div className="tw-mt-1 tw-text-xs tw-text-destructive">{errors[key]}</div>
    ) : null;

  return (
    <ModalShell
      show={show}
      onHide={handleClose}
      busy={saving}
      icon={user ? LuPencil : LuUserPlus}
      title={user ? "Edit user" : "Add a team member"}
      subtitle={
        user
          ? "Update their name and email."
          : "They can sign in with this Microsoft email."
      }
      footer={
        <>
          <div className="tw-text-xs tw-text-muted-foreground">
            <span className="tw-text-destructive">*</span> Required
          </div>
          <div className="tw-flex tw-gap-2">
            <UiButton variant="outline" onClick={handleClose} disabled={saving}>
              Cancel
            </UiButton>
            <UiButton onClick={save} disabled={saving}>
              {saving ? (
                <LuLoader2 className="tw-animate-spin" aria-hidden="true" />
              ) : (
                <LuCheck aria-hidden="true" />
              )}
              {user ? "Save changes" : "Create user"}
            </UiButton>
          </div>
        </>
      }
    >
      <form
        className="tw-space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div>
          <FieldLabel htmlFor="userName">Name</FieldLabel>
          <input id="userName" className={fieldClass("name")} value={form.name} onChange={set("name")} placeholder="Full name" />
          {errorText("name")}
        </div>
        <div>
          <FieldLabel htmlFor="userEmail">Email</FieldLabel>
          <input id="userEmail" type="email" className={fieldClass("email")} value={form.email} onChange={set("email")} placeholder="name@greatmanagerinstitute.com" />
          {errorText("email")}
        </div>
        {isEdit ? (
          <div className="tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-secondary/60 tw-p-4">
            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
              <div>
                <div className="tw-text-xs tw-font-medium tw-text-muted-foreground">Paid leave</div>
                <div className="tw-text-xl tw-font-semibold tw-text-foreground">
                  {user.plBalance ?? 0}
                  <span className="tw-ml-1 tw-text-xs tw-font-normal tw-text-muted-foreground">days</span>
                </div>
              </div>
              <div>
                <div className="tw-text-xs tw-font-medium tw-text-muted-foreground">Optional leave</div>
                <div className="tw-text-xl tw-font-semibold tw-text-foreground">
                  {user.chBalance ?? 0}
                  <span className="tw-ml-1 tw-text-xs tw-font-normal tw-text-muted-foreground">days</span>
                </div>
              </div>
            </div>
            <div className="tw-mt-2 tw-text-xs tw-leading-5 tw-text-muted-foreground">
              Balances update automatically from leave applications and the
              quarterly paid-leave credit, so they can't be edited here.
            </div>
          </div>
        ) : (
          <div>
            <div className="tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
              Opening balances
            </div>
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <div>
                <FieldLabel htmlFor="userPl" hint={`0–${MAX_PL}`}>
                  Paid leave
                </FieldLabel>
                <input id="userPl" type="number" min="0" max={MAX_PL} className={fieldClass("plBalance")} value={form.plBalance} onChange={set("plBalance")} />
                {errorText("plBalance")}
              </div>
              <div>
                <FieldLabel htmlFor="userOptional" hint={`0–${MAX_OPTIONAL}`}>
                  Optional leave
                </FieldLabel>
                <input id="userOptional" type="number" min="0" max={MAX_OPTIONAL} className={fieldClass("chBalance")} value={form.chBalance} onChange={set("chBalance")} />
                {errorText("chBalance")}
              </div>
            </div>
            <div className="tw-mt-2 tw-text-xs tw-text-muted-foreground">
              Set once when the user is created. After that, paid leave is
              credited each quarter and both balances change only through leave.
            </div>
          </div>
        )}
        {/* lets Enter submit the form */}
        <button type="submit" className="tw-hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </ModalShell>
  );
};

const ConfirmStatusModal = ({ user, onCancel, onConfirm, busy }) => {
  const deactivating = !!user?.ifActive;
  return (
    <ModalShell
      show={!!user}
      onHide={onCancel}
      busy={busy}
      icon={deactivating ? LuUserMinus : LuRotateCcw}
      title={deactivating ? "Deactivate user?" : "Reactivate user?"}
      subtitle={user ? `${user.name || user.email}` : ""}
      footer={
        <>
          <span />
          <div className="tw-flex tw-gap-2">
            <UiButton variant="outline" onClick={onCancel} disabled={busy}>
              Cancel
            </UiButton>
            <UiButton
              onClick={onConfirm}
              disabled={busy}
              className={deactivating ? "!tw-bg-none tw-bg-destructive" : ""}
            >
              {busy && <LuLoader2 className="tw-animate-spin" aria-hidden="true" />}
              {deactivating ? "Deactivate" : "Reactivate"}
            </UiButton>
          </div>
        </>
      }
    >
      <div className="tw-text-sm tw-leading-6 tw-text-muted-foreground">
        {deactivating
          ? "They'll be removed from team lists, attendance reports and weekly plans. Their history is kept, and you can reactivate them at any time."
          : "They'll appear in team lists again and can mark attendance as usual."}
      </div>
    </ModalShell>
  );
};

const BalanceCell = ({ value, max, tone }) => {
  const n = Number(value) || 0;
  const pct = Math.max(0, Math.min(100, (n / max) * 100));
  return (
    <div className="tw-flex tw-items-center tw-gap-2">
      <span className={`tw-w-6 tw-text-right tw-text-sm tw-font-semibold ${n === 0 ? "tw-text-muted-foreground" : "tw-text-foreground"}`}>
        {n}
      </span>
      <div className="tw-h-1.5 tw-w-16 tw-overflow-hidden tw-rounded-full tw-bg-secondary">
        <div className={`tw-h-full tw-rounded-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const UserList = () => {
  const [lists, setLists] = useState({ 1: [], 0: [] });
  const [ifActive, setIfActive] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmUser, setConfirmUser] = useState(null);
  const [toggling, setToggling] = useState(false);

  const fetchList = (active) =>
    Axios.get(config.API_BASE + "/user/getUserList?" + queryString.stringify({ ifActive: active }))
      .then((response) => (response.data?.status ? response.data.data || [] : []))
      .catch((error) => {
        console.log("error", error);
        return [];
      });

  // Load both lists so the tab counts are always right
  const getUserList = useCallback(async () => {
    const [active, inactive] = await Promise.all([fetchList(1), fetchList(0)]);
    setLists({ 1: active, 0: inactive });
    setLoading(false);
  }, []);

  useEffect(() => {
    getUserList();
  }, [getUserList]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (lists[ifActive] || [])
      .filter(
        (u) =>
          !q ||
          String(u.name || "").toLowerCase().includes(q) ||
          String(u.email || "").toLowerCase().includes(q),
      )
      .sort((a, b) =>
        String(a.name || a.email || "").localeCompare(String(b.name || b.email || ""), undefined, {
          sensitivity: "base",
        }),
      );
  }, [lists, ifActive, search]);

  const toggleActive = () => {
    if (!confirmUser) return;
    setToggling(true);
    Axios.post(config.API_BASE + "/user/updateUserDetails", {
      ifActive: confirmUser.ifActive ? 0 : 1,
      userId: confirmUser.id,
    })
      .then((response) => {
        if (response.data?.status) {
          toast.success(confirmUser.ifActive ? "User deactivated" : "User reactivated");
          setConfirmUser(null);
          getUserList();
        } else {
          toast.error(response.data?.msg || "Couldn't update this user");
        }
      })
      .catch((error) => {
        console.log("error", error);
        toast.error("Something went wrong. Please try again.");
      })
      .finally(() => setToggling(false));
  };

  const tabs = [
    { value: 1, label: "Active", count: lists[1].length },
    { value: 0, label: "Inactive", count: lists[0].length },
  ];

  return (
    <>
      <Header />
      <div className="container-fluid">
        <div className="tw-mx-auto tw-max-w-7xl tw-py-4 tw-font-sans tw-text-foreground sm:tw-py-6">
          <div className="tw-mb-6 tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3">
            <div>
              <h1 className="tw-m-0 tw-text-2xl tw-font-semibold tw-leading-8 tw-text-foreground">
                Users
              </h1>
              <div className="tw-mt-1 tw-text-sm tw-text-muted-foreground">
                Manage who can use Attendance Tracker. Leave balances update automatically.
              </div>
            </div>
            <UiButton
              onClick={() => {
                setSelectedUser(null);
                setShowModal(true);
              }}
            >
              <LuUserPlus aria-hidden="true" />
              Add user
            </UiButton>
          </div>

          <section className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-shadow-soft">
            <div className="tw-flex tw-flex-col tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-border tw-p-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
              <div className="tw-inline-flex tw-gap-1 tw-rounded-lg tw-bg-secondary tw-p-1" role="tablist" aria-label="User status">
                {tabs.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={ifActive === tab.value}
                    onClick={() => setIfActive(tab.value)}
                    className={`tw-inline-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-md tw-border-0 tw-px-3 tw-text-sm tw-font-medium tw-transition-colors ${
                      ifActive === tab.value
                        ? "tw-bg-white tw-text-foreground tw-shadow-soft"
                        : "tw-bg-transparent tw-text-muted-foreground hover:tw-text-foreground"
                    }`}
                  >
                    {tab.label}
                    <span className="tw-rounded-full tw-bg-secondary tw-px-1.5 tw-text-[11px] tw-font-semibold tw-text-muted-foreground">
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>
              <div className="tw-relative sm:tw-w-72">
                <LuSearch className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-muted-foreground" aria-hidden="true" />
                <input
                  type="search"
                  className={`${INPUT} tw-pl-9`}
                  placeholder="Search name or email"
                  aria-label="Search users"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {loading ? (
              <div className="tw-space-y-2 tw-p-4">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="tw-h-12 tw-animate-pulse tw-rounded-md tw-bg-secondary" />
                ))}
              </div>
            ) : visible.length === 0 ? (
              <div className="tw-flex tw-flex-col tw-items-center tw-px-4 tw-py-12 tw-text-center">
                <LuUsers className="tw-mb-2 tw-text-2xl tw-text-muted-foreground" aria-hidden="true" />
                <div className="tw-text-sm tw-text-muted-foreground">
                  {search ? "No users match your search." : ifActive ? "No active users." : "No inactive users."}
                </div>
              </div>
            ) : (
              <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-border-collapse tw-text-sm">
                  <thead>
                    <tr className="tw-bg-secondary/60 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
                      <th className="tw-px-5 tw-py-2.5 tw-font-semibold">User</th>
                      <th className="tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-font-semibold">Paid leave</th>
                      <th className="tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-font-semibold">Optional leave</th>
                      <th className="tw-px-5 tw-py-2.5 tw-text-right tw-font-semibold">
                        <span className="tw-sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((u) => (
                      <tr
                        key={u.id}
                        className="tw-border-0 tw-border-t tw-border-solid tw-border-border hover:tw-bg-secondary/40"
                      >
                        <td className="tw-px-5 tw-py-3">
                          <div className="tw-flex tw-items-center tw-gap-3">
                            <span
                              className={`tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-semibold ${
                                ifActive ? "tw-bg-gradient-primary tw-text-white" : "tw-bg-secondary tw-text-muted-foreground"
                              }`}
                              aria-hidden="true"
                            >
                              {getInitials(u.name, u.email)}
                            </span>
                            <div className="tw-min-w-0">
                              <div className="tw-truncate tw-font-medium tw-text-foreground">
                                {u.name || <span className="tw-italic tw-text-muted-foreground">No name yet</span>}
                              </div>
                              <div className="tw-truncate tw-text-xs tw-text-muted-foreground">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="tw-px-3 tw-py-3">
                          <BalanceCell value={u.plBalance} max={MAX_PL} tone="tw-bg-gradient-primary" />
                        </td>
                        <td className="tw-px-3 tw-py-3">
                          <BalanceCell value={u.chBalance} max={MAX_OPTIONAL} tone="tw-bg-amber-400" />
                        </td>
                        <td className="tw-whitespace-nowrap tw-px-5 tw-py-3 tw-text-right">
                          <div className="tw-inline-flex tw-gap-1">
                            <UiButton
                              variant="ghost"
                              size="icon"
                              title="Edit"
                              aria-label={`Edit ${u.name || u.email}`}
                              onClick={() => {
                                setSelectedUser(u);
                                setShowModal(true);
                              }}
                            >
                              <LuPencil aria-hidden="true" />
                            </UiButton>
                            {u.ifActive ? (
                              <UiButton
                                variant="danger"
                                size="icon"
                                title="Deactivate"
                                aria-label={`Deactivate ${u.name || u.email}`}
                                onClick={() => setConfirmUser(u)}
                              >
                                <LuUserMinus aria-hidden="true" />
                              </UiButton>
                            ) : (
                              <UiButton
                                variant="ghost"
                                size="icon"
                                title="Reactivate"
                                aria-label={`Reactivate ${u.name || u.email}`}
                                onClick={() => setConfirmUser(u)}
                              >
                                <LuRotateCcw aria-hidden="true" />
                              </UiButton>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>

      <UserFormModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        user={selectedUser}
        onSaved={getUserList}
      />
      <ConfirmStatusModal
        user={confirmUser}
        busy={toggling}
        onCancel={() => setConfirmUser(null)}
        onConfirm={toggleActive}
      />
    </>
  );
};

export default UserList;
